/**
 * Feature/permission key catalog — shared SDK surface.
 *
 * Canonical source of truth: `server/src/domain/enums/permissions/permission-keys.ts`.
 * This is the hand-mirrored copy the web consumes via `@event-manager/api-client`
 * (the role-management widget, feature gating, plan-flag resolution). Keep it in
 * sync with the server file when adding/renaming a key.
 *
 * Long-term this should be generated from a `GET /api/permissions/catalog`
 * endpoint rather than hand-mirrored.
 */

/** Config tab ids surfaced in the settings UI. */
export const CONFIG_TAB_IDS = [
	"ai-assistant",
	"appearance",
	"business",
	"calendar-display",
	"columns",
	"email-templates",
	"event-types",
	"notification-rules",
	"schedule",
	"whatsapp",
] as const;

export type ConfigTabId = (typeof CONFIG_TAB_IDS)[number];

const CONFIG_TAB_KEYS = CONFIG_TAB_IDS.flatMap(
	(id) => [`config:tab:${id}:view`, `config:tab:${id}:edit`] as const
);

export const FEATURE_KEYS = [
	// Calendar — gated by plan `calendar`
	"calendar:edit",
	"calendar:view",
	// Chat — always available (chat plan is the floor)
	"chat:reply",
	"chat:view",
	// Config tabs — not plan-gated; per-tab explicit grants
	...CONFIG_TAB_KEYS,
	// Contacts — always available (chat plan is the floor)
	"contacts:edit",
	"contacts:view",
	"campaigns:manage",
	"campaigns:view",
	// Documents — gated by plan `documents`
	"documents:edit",
	"documents:view",
	"waitlist:manage",
	"waitlist:view",
	"templates:manage",
	"templates:view",
	// Org management — not plan-gated
	"billing:manage",
	"feedback:manage",
	"members:manage",
	"privacy:manage",
	"roles:manage",
	"schedulers:manage",
	"secrets:manage",
	"webhooks:manage",
] as const;

/** TypeScript union of every recognised feature key. */
export type FeatureKey = (typeof FEATURE_KEYS)[number];

/** Type guard for a feature key. */
export function isFeatureKey(value: unknown): value is FeatureKey {
	return (
		typeof value === "string" &&
		(FEATURE_KEYS as readonly string[]).includes(value)
	);
}

/** Plan-tier gate per key. `null` means the key is not plan-gated. */
export type OrgFeatureFlag = "calendar" | "chat" | "documents";

const KEY_TO_PLAN_FLAG_INTERNAL: Record<string, OrgFeatureFlag | null> = {};
for (const key of FEATURE_KEYS) {
	if (key.startsWith("calendar:")) {
		KEY_TO_PLAN_FLAG_INTERNAL[key] = "calendar";
	} else if (key.startsWith("chat:")) {
		KEY_TO_PLAN_FLAG_INTERNAL[key] = "chat";
	} else if (key.startsWith("documents:")) {
		KEY_TO_PLAN_FLAG_INTERNAL[key] = "documents";
	} else {
		KEY_TO_PLAN_FLAG_INTERNAL[key] = null;
	}
}

export const KEY_TO_PLAN_FLAG: Readonly<
	Record<FeatureKey, OrgFeatureFlag | null>
> = KEY_TO_PLAN_FLAG_INTERNAL as Record<FeatureKey, OrgFeatureFlag | null>;

/** Resolve the plan flag for any string key (`null` if unknown or ungated). */
export function planFlagForKey(key: string): OrgFeatureFlag | null {
	return KEY_TO_PLAN_FLAG_INTERNAL[key] ?? null;
}

/** Build the per-tab config feature key. */
export function configTabKey(id: ConfigTabId, op: "view" | "edit"): FeatureKey {
	return `config:tab:${id}:${op}` as FeatureKey;
}

/** TypeScript union of every recognised permission key. */
export type PermissionKey = FeatureKey;

/** Runtime assertion that `value` is a recognised permission key. */
export function assertPermissionKey(
	value: string
): asserts value is PermissionKey {
	if (!(FEATURE_KEYS as readonly string[]).includes(value)) {
		throw new TypeError(`Unknown permission key: ${value}`);
	}
}
